import React, {useCallback, useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {ResponsiveBar} from "@nivo/bar";
import {useAtomValue} from "jotai";
import {BoardSearchResult, ChartTooltip} from "../../assets/GlobalStyles";
import Table from "../../components/table";
import HorizontalScrollGuide from "../../components/table/HorizontalScrollGuide";
import {ReportsCondition} from "../../components/reports/Condition";
import {sort} from "../../components/reports/sortList";
import {decimalFormat} from "../../common/StringUtils";
import {reportsStatics, reportsStaticsHourlyAllColumn, reportsUserStaticsHourlyAllColumn} from "./entity/period";
import {lockedRows, summaryReducer} from "./entity/common";
import {tokenResultAtom} from "../login/entity";
import {selectAdminStaticsHourAll, selectUserStaticsHourlyAll} from "../../services/reports/periodAxios";
import {UserInfo} from "../layout";
import {calculateEcpm, formatEcpm} from "./entity/metrics";
import {
  CollapseIcon, ConditionBody, ConditionSummary, ExcelHeaderButton, HeaderRight,
  ReportCard, ReportPage, SectionAccent, SectionHeader, SectionHeading, SummaryChip, TableBody
} from "./ReportSharedLayout";
import {getReportMetricColor, REPORT_METRIC_TONES} from "../../components/reports/ReportMetricTheme";

const formatRevenue = value => Number(value || 0).toLocaleString("ko-KR", {
  minimumFractionDigits:1, maximumFractionDigits:1
});

function HourChart({selectKey, data, compactYAxis = false}) {
  const formatValue = value => selectKey === "revenueAmount"
      ? formatRevenue(value)
      : selectKey === "ecpm" ? formatEcpm(value) : decimalFormat(value);
  return <ResponsiveBar
    data={data} keys={[selectKey]} indexBy="historyHour" height={340}
    margin={{top:30,right:30,bottom:70,left:64}} padding={0.4}
    valueScale={{type:"linear",min:0,max:"auto",nice:true}} indexScale={{type:"band",round:true}}
    colors={[getReportMetricColor(selectKey)]} enableLabel={false} enableGridY
    axisLeft={{tickSize:0,tickPadding:10,tickValues:compactYAxis?4:5,format:formatValue}}
    axisBottom={{tickSize:0,tickPadding:15,format:value=>`${String(value).padStart(2,"0")}시`}}
    tooltip={({indexValue,value}) => <ChartTooltip><p>{String(indexValue).padStart(2,"0")}시</p><p>{formatValue(value)}</p></ChartTooltip>}
    animate motionStiffness={120} motionDamping={15}
  />
}

export default function ReportsHourPeriod(){
  const [searchCondition,setSearchCondition] = useState(reportsStatics)
  const [searchState,setSearchState] = useState(reportsStatics)
  const [chartKey,setChartKey] = useState("revenueAmount")
  const [totalCount,setTotalCount] = useState(0)
  const [chartData,setChartData] = useState([])
  const [creativeInfo,setCreativeInfo] = useState({})
  const [isConditionOpen,setIsConditionOpen] = useState(false)
  const [downloadTrigger,setDownloadTrigger] = useState(0)
  const tokenInfoState = useAtomValue(tokenResultAtom)
  const userInfoState = useAtomValue(UserInfo)

  const summary = useMemo(() => chartData.reduce((sum,item)=>({
    requestCount:sum.requestCount+(Number(item.requestCount)||0),
    exposureCount:sum.exposureCount+(Number(item.exposureCount)||0),
    revenueAmount:sum.revenueAmount+(Number(item.revenueAmount)||0),
  }),{requestCount:0,exposureCount:0,revenueAmount:0}),[chartData])
  const summaryEcpm = calculateEcpm(summary.revenueAmount,summary.exposureCount)

  const requestData = useCallback(async condition => tokenInfoState.role === "NORMAL"
      ? selectUserStaticsHourlyAll(tokenInfoState.id,condition)
      : selectAdminStaticsHourAll(creativeInfo.id,condition),
    [creativeInfo.id,tokenInfoState.id,tokenInfoState.role])

  const handleSearchCondition = useCallback(async ({skip,limit,sortInfo}) => {
    const response = await requestData({
      ...searchCondition,pageSize:30,currentPage:skip/limit===0?1:skip/limit+1,
      sortType:sort("HOUR_DESC",sortInfo)
    })
    if(!response) return {data:[],count:0}
    setTotalCount(response.totalElements)
    return {data:response.content,count:response.totalElements}
  },[requestData,searchCondition])

  const fetchChart = useCallback(async () => {
    const response = await requestData({...searchCondition,pageSize:24,currentPage:1,sortType:"HOUR_ASC"})
    const data = (response?.content || []).map(item => ({
      ...item, ecpm:calculateEcpm(item.revenueAmount,item.exposureCount)
    }))
    setChartData(data)
  },[requestData,searchCondition])
  useEffect(()=>{fetchChart()},[fetchChart])

  const onSearch = ({mediaInfo}={}) => {
    setCreativeInfo(mediaInfo || {})
    setSearchCondition({...searchState})
  }
  const toggleCondition = () => setIsConditionOpen(open=>!open)

  return <ReportPage>
    <ReportCard $condition>
      <SectionHeader $clickable $open={isConditionOpen} role="button" tabIndex={0}
        aria-expanded={isConditionOpen} onClick={toggleCondition}
        onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();toggleCondition()}}}>
        <SectionHeading><SectionAccent/><div><strong>조회 조건</strong><span>매체, 광고 상품, 디바이스와 기간을 기준으로 시간대별 데이터를 조회합니다.</span></div></SectionHeading>
        <HeaderRight>
          {!isConditionOpen && <ConditionSummary>
            <SummaryChip>매체 <b>{creativeInfo.siteName || "전체"}</b></SummaryChip>
            <SummaryChip>상품 <b>{searchCondition.productType || "전체"}</b></SummaryChip>
            <SummaryChip>디바이스 <b>{searchCondition.deviceType || "전체"}</b></SummaryChip>
            <SummaryChip>기간 <b>{searchCondition.searchStartDate} ~ {searchCondition.searchEndDate}</b></SummaryChip>
          </ConditionSummary>}
          <CollapseIcon $open={isConditionOpen}/>
        </HeaderRight>
      </SectionHeader>
      {isConditionOpen && <ConditionBody onClick={e=>e.stopPropagation()}>
        <ReportsCondition modern searchMediaInfo={creativeInfo} searchState={searchState}
          setSearchState={setSearchState} onSearch={onSearch}/>
      </ConditionBody>}
    </ReportCard>

    <ReportCard>
      <ChartBody>
        <ChartTop>
          <div><CardTitle>차트 지표</CardTitle><CardDescription>확인할 지표를 선택하면 시간대별 차트에 반영됩니다.</CardDescription></div>
          <KpiGrid>
            <KpiCard $tone="blue"><span>요청</span><div><small>총 요청수</small><strong>{decimalFormat(summary.requestCount)}</strong><em>건</em></div></KpiCard>
            <KpiCard $tone="cyan"><span>노출</span><div><small>총 노출수</small><strong>{decimalFormat(summary.exposureCount)}</strong><em>건</em></div></KpiCard>
            <KpiCard $tone="green"><span>수익</span><div><small>총 수익금</small><strong>{formatRevenue(summary.revenueAmount)}</strong><em>원</em></div></KpiCard>
            {userInfoState.email === "" && <KpiCard $tone="purple"><span>eCPM</span><div><small>평균 eCPM</small><strong>{formatEcpm(summaryEcpm)}</strong><em>원</em></div></KpiCard>}
          </KpiGrid>
        </ChartTop>
        <MetricButtons>
          {[["requestCount","요청수"],["exposureCount","노출수"],["revenueAmount","수익금"]].map(([key,label])=>
            <MetricButton type="button" key={key} $metric={key} $active={chartKey===key} onClick={()=>setChartKey(key)}><i/>{label}</MetricButton>)}
          {userInfoState.email === "" && <MetricButton type="button" $metric="ecpm" $active={chartKey==="ecpm"} onClick={()=>setChartKey("ecpm")}><i/>ECPM</MetricButton>}
        </MetricButtons>
        <ChartArea><HourChart selectKey={chartKey} data={chartData} compactYAxis={tokenInfoState.role === "NORMAL"}/></ChartArea>
      </ChartBody>
    </ReportCard>

    <ReportCard>
      <SectionHeader>
        <SectionHeading><SectionAccent/><div><strong>시간대별 데이터</strong><span>조회 조건에 해당하는 시간대별 상세 성과를 확인합니다.</span></div></SectionHeading>
        <ExcelHeaderButton type="button" onClick={()=>setDownloadTrigger(v=>v+1)}>엑셀 저장</ExcelHeaderButton>
      </SectionHeader>
      <TableBody><HorizontalScrollGuide/><BoardSearchResult>
        <Table columns={userInfoState.email === "" ? reportsStaticsHourlyAllColumn : reportsUserStaticsHourlyAllColumn}
          lockedRows={lockedRows} summaryReducer={summaryReducer} totalCount={[totalCount,"보고서"]}
          data={handleSearchCondition} defaultSortInfo={{name:"historyHour",dir:-1}}
          pagination livePagination downloadList={false} downloadTrigger={downloadTrigger}
          fileName="시간대별_보고서" scrollThreshold={0.7} headerHeight={44} style={{minHeight:500}}/>
      </BoardSearchResult></TableBody>
    </ReportCard>
  </ReportPage>
}

const ChartBody=styled.div`padding:18px 20px 20px;`;
const ChartTop=styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:20px;@media(max-width:900px){flex-direction:column;}`;
const CardTitle=styled.strong`display:block;color:#27313c;font-size:13px;font-weight:750;`;
const CardDescription=styled.span`display:block;margin-top:5px;color:#8a949f;font-size:10px;`;
const KpiGrid=styled.div`display:grid;grid-template-columns:repeat(4,minmax(120px,1fr));gap:7px;@media(max-width:1100px){grid-template-columns:repeat(2,minmax(130px,1fr));}`;
const tones=REPORT_METRIC_TONES;
const KpiCard=styled.div`display:flex;min-width:0;height:50px;align-items:center;gap:8px;padding:7px 9px;border:1px solid ${({$tone})=>tones[$tone][2]};border-radius:8px;background:${({$tone})=>tones[$tone][1]};>span{display:inline-flex;width:30px;height:30px;flex:0 0 30px;align-items:center;justify-content:center;border-radius:7px;background:#fff;color:${({$tone})=>tones[$tone][0]};font-size:8px;font-weight:800;}div{min-width:0;}small{display:block;margin-bottom:2px;color:#7f8995;font-size:8px;}strong{color:#293644;font-size:12px;}em{margin-left:3px;color:#7f8995;font-size:8px;font-style:normal;}`;
const MetricButtons=styled.div`display:flex;align-items:center;gap:8px;margin-top:14px;flex-wrap:wrap;`;
const MetricButton=styled.button`display:inline-flex;height:30px;align-items:center;gap:6px;padding:0 10px;border:0;border-radius:6px;background:${({$active,$metric})=>$active?REPORT_METRIC_TONES[$metric][1]:"transparent"};color:${({$active,$metric})=>$active?REPORT_METRIC_TONES[$metric][0]:"#66727f"};font-size:10px;font-weight:700;cursor:pointer;i{width:7px;height:7px;border-radius:2px;background:${({$metric})=>REPORT_METRIC_TONES[$metric][0]};}`;
const ChartArea=styled.div`
  width:100%;height:375px;min-height:375px;margin-top:5px;overflow:hidden;
`;
